import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const RetailPartnerPage = () => {
  useEffect(() => {
    document.title = 'Retail Partnership Program | Only Skullz';
  }, []);

  return (
    <>
      <style>{`
        .retail-partner-page * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        .retail-partner-page {
          font-family: 'Inter', sans-serif;
          background: #0a0a0a;
          color: #fff;
          min-height: 100vh;
          overflow-x: hidden;
          position: relative;
        }
        
        /* Animated background gradient */
        .retail-partner-page .bg-animation {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: 
            radial-gradient(circle at 20% 50%, rgba(139, 0, 0, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(139, 0, 0, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(40, 0, 0, 0.4) 0%, #0a0a0a 70%);
          z-index: 0;
        }
        
        /* Noise texture overlay */
        .retail-partner-page .noise {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0.03;
          z-index: 1;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          pointer-events: none;
        }
        
        .retail-partner-page .rp-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          position: relative;
          z-index: 2;
        }
        
        /* Header Section */
        .retail-partner-page .rp-header {
          text-align: center;
          margin-bottom: 50px;
          animation: rpFadeInDown 1s ease-out;
        }
        
        .retail-partner-page .rp-logo {
          font-family: 'Cinzel', serif;
          font-size: clamp(2rem, 5vw, 3.5rem);
          font-weight: 900;
          letter-spacing: 4px;
          text-transform: uppercase;
          margin-bottom: 10px;
          background: linear-gradient(135deg, #fff 0%, #8b0000 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-shadow: 0 0 40px rgba(139, 0, 0, 0.3);
        }
        
        .retail-partner-page .rp-tagline {
          font-size: 1.1rem;
          color: #888;
          letter-spacing: 3px;
          text-transform: uppercase;
          font-weight: 300;
        }
        
        .retail-partner-page .decision-title {
          font-family: 'Cinzel', serif;
          font-size: clamp(1.5rem, 4vw, 2.5rem);
          text-align: center;
          margin: 40px 0 50px;
          color: #fff;
          letter-spacing: 2px;
          position: relative;
          animation: rpFadeIn 1.2s ease-out;
        }
        
        .retail-partner-page .decision-title::after {
          content: '';
          display: block;
          width: 100px;
          height: 2px;
          background: linear-gradient(90deg, transparent, #8b0000, transparent);
          margin: 20px auto 0;
        }
        
        /* Cards Container */
        .retail-partner-page .cards-wrapper {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          position: relative;
          animation: rpFadeInUp 1s ease-out 0.3s both;
        }
        
        @media (max-width: 768px) {
          .retail-partner-page .cards-wrapper {
            grid-template-columns: 1fr;
          }
        }
        
        /* VS Badge */
        .retail-partner-page .vs-badge {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 60px;
          height: 60px;
          background: #8b0000;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Cinzel', serif;
          font-weight: 900;
          font-size: 1.2rem;
          border: 3px solid #0a0a0a;
          z-index: 10;
          box-shadow: 0 0 30px rgba(139, 0, 0, 0.6);
        }
        
        @media (max-width: 768px) {
          .retail-partner-page .vs-badge {
            display: none;
          }
        }
        
        /* Card Styles */
        .retail-partner-page .rp-card {
          background: rgba(20, 20, 20, 0.6);
          border: 1px solid rgba(139, 0, 0, 0.3);
          border-radius: 16px;
          padding: 40px 30px;
          position: relative;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          backdrop-filter: blur(10px);
        }
        
        .retail-partner-page .rp-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, transparent, #8b0000, transparent);
          opacity: 0.5;
          transition: opacity 0.3s;
        }
        
        .retail-partner-page .rp-card:hover {
          transform: translateY(-10px);
          border-color: rgba(139, 0, 0, 0.8);
          box-shadow: 
            0 20px 40px rgba(0, 0, 0, 0.5),
            0 0 60px rgba(139, 0, 0, 0.2);
        }
        
        .retail-partner-page .rp-card:hover::before {
          opacity: 1;
          height: 6px;
        }
        
        .retail-partner-page .card-header {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .retail-partner-page .card-icon {
          font-size: 3rem;
          margin-bottom: 15px;
          display: block;
        }
        
        .retail-partner-page .card-title {
          font-family: 'Cinzel', serif;
          font-size: 1.8rem;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 5px;
        }
        
        .retail-partner-page .card-subtitle {
          color: #8b0000;
          font-size: 0.9rem;
          letter-spacing: 1px;
          font-weight: 600;
        }
        
        /* Percentage Highlight */
        .retail-partner-page .percentage-box {
          background: linear-gradient(135deg, rgba(139, 0, 0, 0.2), rgba(139, 0, 0, 0.05));
          border: 2px solid #8b0000;
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          margin: 25px 0;
          position: relative;
          overflow: hidden;
        }
        
        .retail-partner-page .percentage-box::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, transparent, rgba(255,255,255,0.05), transparent);
          transform: rotate(45deg);
          animation: rpShimmer 3s infinite;
        }
        
        @keyframes rpShimmer {
          0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
          100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
        }
        
        .retail-partner-page .percentage-label {
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: #888;
          margin-bottom: 5px;
          position: relative;
          z-index: 1;
        }
        
        .retail-partner-page .percentage-value {
          font-family: 'Cinzel', serif;
          font-size: 2.5rem;
          font-weight: 900;
          color: #fff;
          text-shadow: 0 0 20px rgba(139, 0, 0, 0.5);
          position: relative;
          z-index: 1;
          line-height: 1;
        }
        
        .retail-partner-page .percentage-suffix {
          font-size: 1.2rem;
          color: #8b0000;
          font-weight: 700;
        }
        
        .retail-partner-page .percentage-note {
          font-size: 0.75rem;
          color: #666;
          margin-top: 8px;
          font-style: italic;
        }
        
        /* Features List */
        .retail-partner-page .features {
          list-style: none;
          margin: 25px 0;
          padding: 0;
        }
        
        .retail-partner-page .features li {
          padding: 12px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 0.95rem;
          color: #ccc;
        }
        
        .retail-partner-page .features li:last-child {
          border-bottom: none;
        }
        
        .retail-partner-page .features li::before {
          content: '▸';
          color: #8b0000;
          font-weight: bold;
          font-size: 1.2rem;
        }
        
        .retail-partner-page .features .highlight {
          color: #fff;
          font-weight: 600;
        }
        
        /* Agreement Button */
        .retail-partner-page .agreement-btn {
          display: block;
          width: 100%;
          padding: 14px 20px;
          margin-top: 20px;
          background: linear-gradient(135deg, #8b0000 0%, #5a0000 100%);
          color: #fff;
          text-decoration: none;
          text-align: center;
          font-family: 'Cinzel', serif;
          font-size: 0.9rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(139, 0, 0, 0.3);
        }
        
        .retail-partner-page .agreement-btn:hover {
          background: linear-gradient(135deg, #a50000 0%, #700000 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(139, 0, 0, 0.5);
        }
        
        .retail-partner-page .agreement-btn:active {
          transform: translateY(0);
        }
        
        /* Comparison Bar */
        .retail-partner-page .comparison-bar {
          margin-top: 60px;
          padding: 30px;
          background: rgba(139, 0, 0, 0.1);
          border: 1px solid rgba(139, 0, 0, 0.3);
          border-radius: 12px;
          text-align: center;
          animation: rpFadeIn 1s ease-out 0.6s both;
        }
        
        .retail-partner-page .comparison-title {
          font-family: 'Cinzel', serif;
          font-size: 1.2rem;
          margin-bottom: 20px;
          color: #fff;
        }
        
        .retail-partner-page .comparison-grid {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          gap: 20px;
          align-items: center;
          max-width: 600px;
          margin: 0 auto;
        }
        
        .retail-partner-page .comparison-item {
          padding: 15px;
        }
        
        .retail-partner-page .comparison-item.left {
          text-align: right;
          border-right: 2px solid #8b0000;
        }
        
        .retail-partner-page .comparison-item.right {
          text-align: left;
          border-left: 2px solid #8b0000;
        }
        
        .retail-partner-page .comparison-label {
          font-size: 0.8rem;
          color: #888;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 5px;
        }
        
        .retail-partner-page .comparison-value {
          font-family: 'Cinzel', serif;
          font-size: 1.5rem;
          font-weight: 700;
          color: #fff;
        }
        
        .retail-partner-page .comparison-arrow {
          font-size: 2rem;
          color: #8b0000;
          animation: rpPulse 2s infinite;
        }
        
        @keyframes rpPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }
        
        /* Footer */
        .retail-partner-page .rp-footer {
          text-align: center;
          margin-top: 50px;
          padding-top: 30px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          color: #666;
          font-size: 0.9rem;
          animation: rpFadeIn 1s ease-out 0.9s both;
        }
        
        .retail-partner-page .rp-footer a {
          color: #8b0000;
          text-decoration: none;
          font-weight: 600;
        }
        
        .retail-partner-page .rp-footer a:hover {
          text-decoration: underline;
        }
        
        /* Animations */
        @keyframes rpFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes rpFadeInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes rpFadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Mobile optimizations */
        @media (max-width: 480px) {
          .retail-partner-page .rp-card {
            padding: 30px 20px;
          }
          
          .retail-partner-page .percentage-value {
            font-size: 2rem;
          }
          
          .retail-partner-page .comparison-grid {
            grid-template-columns: 1fr;
            gap: 15px;
          }
          
          .retail-partner-page .comparison-item.left,
          .retail-partner-page .comparison-item.right {
            text-align: center;
            border: none;
            border-bottom: 2px solid #8b0000;
          }
          
          .retail-partner-page .comparison-item.right {
            border-bottom: none;
            border-top: 2px solid #8b0000;
          }
          
          .retail-partner-page .comparison-arrow {
            transform: rotate(90deg);
          }
        }
      `}</style>
      
      <div className="retail-partner-page">
        <div className="bg-animation"></div>
        <div className="noise"></div>
        
        <div className="rp-container">
          <header className="rp-header">
            <h1 className="rp-logo">Only Skullz</h1>
            <p className="rp-tagline">Retail Partnership Program</p>
          </header>
          
          <h2 className="decision-title">Choose Your Business Model</h2>
          
          <div className="cards-wrapper">
            <div className="vs-badge">VS</div>
            
            {/* Consignment Card */}
            <article className="rp-card">
              <div className="card-header">
                <span className="card-icon">📦</span>
                <h3 className="card-title">Consignment</h3>
                <p className="card-subtitle">Low Risk • Pay As You Sell</p>
              </div>
              
              <div className="percentage-box">
                <div className="percentage-label">Commission Range</div>
                <div className="percentage-value">10-15<span className="percentage-suffix">%</span></div>
                <div className="percentage-note">Per item sold</div>
              </div>
              
              <ul className="features">
                <li><span className="highlight">No upfront cost</span> — we supply the products</li>
                <li>Pay only for what sells monthly</li>
                <li>We retain ownership until sale</li>
                <li>Monthly reporting required</li>
                <li>90-day initial term</li>
                <li>Best for <span className="highlight">testing new markets</span></li>
              </ul>
              
              <Link 
                to="/consignment-agreement" 
                className="agreement-btn"
                data-testid="consignment-agreement-btn"
              >
                View & Print Agreement
              </Link>
            </article>
            
            {/* Wholesale Card */}
            <article className="rp-card">
              <div className="card-header">
                <span className="card-icon">⚡</span>
                <h3 className="card-title">Wholesale</h3>
                <p className="card-subtitle">Higher Margin • Full Control</p>
              </div>
              
              <div className="percentage-box">
                <div className="percentage-label">Discount Range</div>
                <div className="percentage-value">20-40<span className="percentage-suffix">%</span></div>
                <div className="percentage-note">Off retail pricing</div>
              </div>
              
              <ul className="features">
                <li><span className="highlight">Higher profit margins</span> — 20-40% off retail</li>
                <li>Immediate inventory ownership</li>
                <li>Set your own retail prices</li>
                <li>Payment due at order</li>
                <li>No monthly reporting hassle</li>
                <li>Best for <span className="highlight">established retailers</span></li>
              </ul>
              
              <Link 
                to="/wholesale-agreement" 
                className="agreement-btn"
                data-testid="wholesale-agreement-btn"
              >
                View & Print Agreement
              </Link>
            </article>
          </div>
          
          {/* Quick Comparison */}
          <div className="comparison-bar">
            <h3 className="comparison-title">The Breakdown</h3>
            <div className="comparison-grid">
              <div className="comparison-item left">
                <div className="comparison-label">Consignment Commission</div>
                <div className="comparison-value">10-15%</div>
              </div>
              <div className="comparison-arrow">→</div>
              <div className="comparison-item right">
                <div className="comparison-label">Wholesale Discount</div>
                <div className="comparison-value">20-40%</div>
              </div>
            </div>
            <p style={{ marginTop: '20px', color: '#888', fontSize: '0.9rem' }}>
              <strong style={{ color: '#8b0000' }}>The gap:</strong> Wholesale discounts double your earnings potential
            </p>
          </div>
          
          <footer className="rp-footer">
            <p>Questions? Contact us at <a href="mailto:Angel@OnlySkullz.com">Angel@OnlySkullz.com</a></p>
            <p style={{ marginTop: '10px', fontSize: '0.8rem' }}>Only Skullz LLC • Aurora, IL</p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default RetailPartnerPage;
